import React, {useState} from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {
  TABLE_AXIS_CELL_HEIGHT,
  TABLE_FIRST_COL_WIDTH,
  TABLE_HEADER_HEIGHT,
  TABLE_ADD_ROW_DRAGGER_HEIGHT,
  TABLE_ROW_HEIGHT,
  FORMULA_BOX_HEIGHT_INLINE,
  DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT,
  TABLE_FOOTER_HEIGHT,
} from 'notebook/tables/TableConstants';
import {GRAY, GRAY_LIGHTER} from 'ui-library/Colors';

const SKELETON_CELL_WIDTH = 78;
const TABLE_PADDING = 10;
const TABLE_PADDING_BOTTOM = 20;

interface TableSkeletonProps {
  height: number;
  bottomSpacer?: number;
}

export default function TableSkeleton({height, bottomSpacer = 0}: TableSkeletonProps): JSX.Element {
  const [cellId] = useState(() => _.uniqueId('cell'));
  const [rowId] = useState(() => _.uniqueId('row'));
  const tablePaddingBottom = TABLE_PADDING_BOTTOM + bottomSpacer;
  const tableHeight = height - TABLE_PADDING - tablePaddingBottom;
  return (
    <div
      style={{
        paddingTop: TABLE_PADDING,
        paddingLeft: TABLE_PADDING,
        paddingRight: TABLE_PADDING,
        paddingBottom: tablePaddingBottom,
      }}
    >
      <div
        style={{
          height: tableHeight,
          width: '100%',
          borderRadius: '4px 4px 0px 0px',
          border: `1px solid ${GRAY}`,
        }}
      >
        <svg width="100%" height="100%">
          <defs>
            <pattern
              id={cellId}
              height={TABLE_ROW_HEIGHT}
              width={SKELETON_CELL_WIDTH}
              x={TABLE_FIRST_COL_WIDTH}
              y="0"
              patternUnits="userSpaceOnUse"
            >
              <line x1="0" y1="0" x2="0" y2={TABLE_ROW_HEIGHT} stroke={GRAY} strokeWidth={1} />
            </pattern>
            <pattern
              id={rowId}
              height={TABLE_ROW_HEIGHT}
              width="100%"
              x="0"
              y={TABLE_HEADER_HEIGHT}
              patternUnits="userSpaceOnUse"
            >
              <rect x="0" y="0" width={TABLE_FIRST_COL_WIDTH} height={TABLE_ROW_HEIGHT} fill={GRAY_LIGHTER} />
              <line x1="0" y1="0" x2="100%" y2="0" stroke={GRAY} strokeWidth={1} />
              <rect
                width="100%"
                height={TABLE_ROW_HEIGHT}
                x={TABLE_FIRST_COL_WIDTH}
                y="0"
                fill={`url(#${cellId})`}
              />
            </pattern>
          </defs>
          <rect width="100%" height={TABLE_ROW_HEIGHT} x="0" y={TABLE_HEADER_HEIGHT} fill={GRAY_LIGHTER} />
          <rect width="100%" height="100%" x="0" y={TABLE_HEADER_HEIGHT} fill={`url(#${rowId})`} />
        </svg>
      </div>
    </div>
  );
}

export function getTableSkeletonHeight(rowCount: number, collapsed: boolean): number {
  // This estimate will provide a lower bound for the height of the table. Often the contents in the cell
  // will wordwrap or include attachments which this estimate doesn't account for.
  if (collapsed) {
    return TABLE_HEADER_HEIGHT + TABLE_PADDING + TABLE_PADDING_BOTTOM;
  }
  if (BenchlingConfig.ENABLE_TABLE_FULLY_EXPANDED_EVERYWHERE) {
    return (
      TABLE_PADDING +
      TABLE_HEADER_HEIGHT +
      FORMULA_BOX_HEIGHT_INLINE +
      TABLE_AXIS_CELL_HEIGHT +
      TABLE_ROW_HEIGHT * rowCount +
      TABLE_FOOTER_HEIGHT +
      TABLE_PADDING_BOTTOM
    );
  }
  if (BenchlingConfig.ENABLE_TABLE_FIXED_HEIGHT_EVERYWHERE) {
    return (
      TABLE_PADDING +
      TABLE_HEADER_HEIGHT +
      FORMULA_BOX_HEIGHT_INLINE +
      TABLE_AXIS_CELL_HEIGHT +
      Math.min(TABLE_ROW_HEIGHT * rowCount, DEFAULT_MAXIMUM_FIXED_TABLE_HEIGHT) +
      TABLE_FOOTER_HEIGHT +
      TABLE_PADDING_BOTTOM
    );
  }
  // TODO(BNCH-115474): Delete this other path once we deprecate expanded inline tables
  return (
    TABLE_PADDING +
    TABLE_HEADER_HEIGHT +
    TABLE_AXIS_CELL_HEIGHT +
    TABLE_ROW_HEIGHT * rowCount +
    TABLE_ADD_ROW_DRAGGER_HEIGHT +
    TABLE_PADDING_BOTTOM
  );
}
